import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { getDailySearchLimit, getMaxMonitorings, isExportEnabled, isEmailAlertsEnabled } from '@/lib/subscriptions';

interface SubscriptionContextType {
  isSubscribed: boolean;
  productId: string | null;
  subscriptionEnd: string | null;
  dailySearchLimit: number | null;
  todaySearchCount: number;
  remainingSearches: number | null;
  maxMonitorings: number | null;
  exportEnabled: boolean;
  emailAlertsEnabled: boolean;
  isAdmin: boolean;
  loading: boolean;
  refreshSubscription: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user, session } = useAuth();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [productId, setProductId] = useState<string | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [dailySearchLimit, setDailySearchLimit] = useState<number | null>(null);
  const [todaySearchCount, setTodaySearchCount] = useState(0);
  const [remainingSearches, setRemainingSearches] = useState<number | null>(null);
  const [maxMonitorings, setMaxMonitorings] = useState<number | null>(1);
  const [exportEnabled, setExportEnabled] = useState(false);
  const [emailAlertsEnabled, setEmailAlertsEnabled] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const refreshSubscription = useCallback(async () => {
    // Get fresh session to avoid using stale tokens during refresh
    const { data: sessionData } = await supabase.auth.getSession();
    const currentSession = sessionData?.session;
    
    if (!currentSession?.access_token) {
      setIsSubscribed(false);
      setProductId(null);
      setSubscriptionEnd(null);
      setDailySearchLimit(3); // Free users get 3 searches
      setTodaySearchCount(0);
      setRemainingSearches(3);
      setExportEnabled(false);
      setEmailAlertsEnabled(false);
      setIsAdmin(false);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: {
          Authorization: `Bearer ${currentSession.access_token}`,
        },
      });

      if (error) throw error;

      const currentProductId = data?.product_id || null;
      const userIsAdmin = data?.is_admin || false;
      
      setIsSubscribed(data?.subscribed || false);
      setProductId(currentProductId);
      setSubscriptionEnd(data?.subscription_end || null);
      setDailySearchLimit(data?.daily_search_limit ?? 3);
      setTodaySearchCount(data?.today_search_count ?? 0);
      setIsAdmin(userIsAdmin);
      
      // Admins get unlimited everything
      if (userIsAdmin) {
        setMaxMonitorings(null); // unlimited
        setExportEnabled(true);
        setEmailAlertsEnabled(true);
        setRemainingSearches(null); // unlimited
      } else {
        setMaxMonitorings(getMaxMonitorings(currentProductId));
        setExportEnabled(isExportEnabled(currentProductId));
        setEmailAlertsEnabled(isEmailAlertsEnabled(currentProductId));
        
        const limit = data?.daily_search_limit;
        const count = data?.today_search_count ?? 0;
        if (limit === null) {
          setRemainingSearches(null); // Unlimited
        } else {
          setRemainingSearches(Math.max(0, (limit ?? 3) - count));
        }
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
      // Fallback to free tier
      setDailySearchLimit(3);
      setRemainingSearches(3);
      setMaxMonitorings(1);
      setExportEnabled(false);
      setEmailAlertsEnabled(false);
      setIsAdmin(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSubscription();
  }, [refreshSubscription]);

  // Auto-refresh every minute
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(() => {
      refreshSubscription();
    }, 60000);

    return () => clearInterval(interval);
  }, [user, refreshSubscription]);

  return (
    <SubscriptionContext.Provider value={{
      isSubscribed,
      productId,
      subscriptionEnd,
      dailySearchLimit,
      todaySearchCount,
      remainingSearches,
      maxMonitorings,
      exportEnabled,
      emailAlertsEnabled,
      isAdmin,
      loading,
      refreshSubscription,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}
